import { useEffect, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ChevronRight, FileText, Loader2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { PackBlockers } from "@/components/pack/PackBlockers"
import { useBidArtifacts } from "@/hooks/useBidArtifacts"
import { useSubmissionPackStore } from "@/stores/submission-pack-store"
import { useArtifactReviewsStore } from "@/stores/artifact-reviews-store"
import { bidSectionPath } from "@/lib/bid-sections"
import type { ArtifactReview, BidArtifact } from "@/lib/api"

const EMPTY_REVIEWS: ArtifactReview[] = []

type BadgeVariant = "secondary" | "warning" | "destructive" | "success"

const REVIEW_STATE: Record<BidArtifact["status"], { label: string; variant: BadgeVariant; dot: boolean }> = {
  draft: { label: "Not sent for review", variant: "secondary", dot: true },
  in_progress: { label: "Not sent for review", variant: "secondary", dot: true },
  pending_review: { label: "In review", variant: "warning", dot: true },
  changes_requested: { label: "Changes requested", variant: "destructive", dot: true },
  finalized: { label: "Approved", variant: "success", dot: true },
}

/** Where "Read the whole response" goes: schedules and forms have no reader, so their own page. */
function readerPath(bidId: string, artifact: BidArtifact): string {
  const base = `/app/bids/${bidId}/responses/${artifact.id}`
  if (artifact.artifactType === "excel_schedule") return `${base}/schedule`
  if (artifact.artifactType === "word_form") return `${base}/form`
  return `${base}/document`
}

function ReviewRow({ bidId, artifact }: { bidId: string; artifact: BidArtifact }) {
  // The same reviews ReviewStatusBanner reads, so the two always agree.
  const reviews = useArtifactReviewsStore((s) => s.byArtifactId[artifact.id] ?? EMPTY_REVIEWS)
  const load = useArtifactReviewsStore((s) => s.load)
  const hasReviewState = artifact.status === "pending_review" || artifact.status === "changes_requested" || artifact.status === "finalized"

  useEffect(() => {
    if (hasReviewState) void load(artifact.id).catch(() => {})
  }, [artifact.id, hasReviewState, load])

  const detail = useMemo(() => {
    const pending = reviews.find((r) => r.status === "pending")
    const completed = reviews.find((r) => r.status === "completed")
    const review = artifact.status === "pending_review" ? pending : completed
    if (!review || !hasReviewState) return null
    const who = review.reviewerFullName || review.reviewerEmail || "Reviewer"
    return `Round ${review.round} · ${who}`
  }, [reviews, artifact.status, hasReviewState])

  const state = REVIEW_STATE[artifact.status]

  return (
    <li className="flex items-center gap-3 px-3 py-3">
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-foreground">{artifact.name}</p>
        {detail && <p className="text-meta">{detail}</p>}
      </div>
      <Badge variant={state.variant} dot={state.dot} className="shrink-0 text-xs">
        {state.label}
      </Badge>
      <Link
        to={readerPath(bidId, artifact)}
        className="inline-flex shrink-0 items-center gap-0.5 text-sm text-[hsl(var(--link))] hover:underline"
      >
        Read the whole response
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </li>
  )
}

/**
 * Review: what stops the bid being submitted (today's pack blockers band),
 * then each response document's review state with a way into the reader.
 */
export function ReviewSection({ bidId }: { bidId: string }) {
  const navigate = useNavigate()
  const pack = useSubmissionPackStore((s) => (s.pack && s.bidId === bidId ? s.pack : null))
  const loadPack = useSubmissionPackStore((s) => s.load)
  const { artifacts, isLoading } = useBidArtifacts(bidId)

  useEffect(() => {
    void loadPack(bidId)
  }, [bidId, loadPack])

  // The chips filter the pack list on Submit; from here they open it.
  const openPack = () => void navigate(bidSectionPath(bidId, "submit"))

  return (
    <div className="max-w-4xl space-y-6">
      {pack && (
        <PackBlockers
          bidId={bidId}
          blockers={pack.blockers}
          needsCount={pack.needsFromYou.length}
          onShowToDo={openPack}
          onShowOverLimit={openPack}
          onShowNeeds={openPack}
        />
      )}

      <section aria-label="Documents in review">
        <h2 className="text-sm font-medium">Documents</h2>
        {isLoading && artifacts.length === 0 ? (
          <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground" role="status">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading documents...
          </div>
        ) : artifacts.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            No response documents yet. Set them up in{" "}
            <Link to={bidSectionPath(bidId, "write")} className="text-[hsl(var(--link))] hover:underline">
              Write
            </Link>
            .
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-border rounded-xl border border-border bg-card shadow-sm">
            {artifacts.map((artifact) => (
              <ReviewRow key={artifact.id} bidId={bidId} artifact={artifact} />
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
